import clsx from 'clsx';
import { AlertTriangle, Check, Facebook, Image as ImageIcon } from 'lucide-react';
import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner/LoadingSpinner';
import { useMetaPagePosts } from '../../../../api/campaigns-queries';
import styles from './MetaPostPicker.module.scss';

interface MetaPostPickerProps {
  /** UUID of the connected Meta ad account. */
  adAccountId: string;
  /** Meta Page whose posts are offered. Posts belong to exactly one Page. */
  pageId?: string;
  /** Currently selected post id (`{page-id}_{post-id}`), from form state. */
  value?: string;
  onChange: (postId: string) => void;
  /** Validation error from the form. */
  error?: string;
}

/** Post copy is often long; the card shows a short lead-in. */
function postPreview(message?: string): string {
  if (!message) return 'Post without text';
  return message;
}

function formatPostedAt(createdTime?: string): string | undefined {
  if (!createdTime) return undefined;
  const date = new Date(createdTime);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Lets the organizer promote a post their Page already published, instead of
 * uploading fresh creative. The resulting ad reuses the post itself, so paid
 * reach lands on the organic post and its likes, comments and shares keep
 * accumulating in one place rather than splitting across two copies.
 */
export function MetaPostPicker({
  adAccountId,
  pageId,
  value,
  onChange,
  error,
}: MetaPostPickerProps) {
  const { data, isLoading, isError, error: queryError } = useMetaPagePosts(adAccountId, pageId);
  const posts = data?.posts ?? [];

  if (!pageId) {
    return (
      <div className={clsx(styles.container, styles.emptyState)}>
        <Facebook size={16} />
        <span>Pick the Facebook Page first — posts belong to a single Page.</span>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.container}>
        <LoadingSpinner />
        <span className={styles.helper}>Loading posts from your Page…</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={clsx(styles.container, styles.errorState)}>
        <AlertTriangle size={16} />
        <span>
          Couldn&apos;t load Page posts. {queryError instanceof Error ? queryError.message : ''}
        </span>
      </div>
    );
  }

  if (posts.length === 0) {
    // The backend distinguishes a token that predates the Page-content
    // permission from a Page that simply has nothing to promote.
    if (data?.reason === 'MISSING_SCOPE') {
      return (
        <div className={clsx(styles.container, styles.emptyState)}>
          <AlertTriangle size={16} />
          <div>
            <strong>Reconnect Meta to boost existing posts.</strong>
            <p>
              Your Meta connection predates the permission that reads Page posts. Reconnect and
              approve it to pick a post here.{' '}
              {data.reconnectUrl && (
                <a href={data.reconnectUrl} rel="noopener noreferrer">
                  Reconnect Meta →
                </a>
              )}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className={clsx(styles.container, styles.emptyState)}>
        <Facebook size={16} />
        <div>
          <strong>This Page has no posts to boost yet.</strong>
          <p>Publish a post on the Page — or switch to building new creative for this campaign.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.list} role="radiogroup" aria-label="Page posts to boost">
        {posts.map((post) => {
          const selected = post.id === value;
          const postedAt = formatPostedAt(post.createdTime);
          return (
            <button
              key={post.id}
              type="button"
              role="radio"
              aria-checked={selected}
              className={clsx(styles.post, selected && styles.postSelected)}
              onClick={() => onChange(post.id)}
            >
              {post.imageUrl ? (
                <img className={styles.thumb} src={post.imageUrl} alt="" />
              ) : (
                <span className={clsx(styles.thumb, styles.thumbPlaceholder)}>
                  <ImageIcon size={20} />
                </span>
              )}
              <span className={styles.postBody}>
                <span className={styles.postMessage}>{postPreview(post.message)}</span>
                {postedAt && <span className={styles.postMeta}>Posted {postedAt}</span>}
              </span>
              {selected && <Check size={16} className={styles.postIndicator} />}
            </button>
          );
        })}
      </div>
      {error && <span className={styles.errorMsg}>{error}</span>}
    </div>
  );
}
